import type { LucideIcon } from 'lucide-react'
import {
  User,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Calendar,
  Briefcase,
  Home,
  Shield,
  Clipboard,
  MousePointerClick,
  FileText,
} from 'lucide-react'

import type { MapItem } from '../utils'

import { appraiserTransactionTypeMap } from './referral-fields/appraiser/appraiser-transaction-type'
import { attorneyTransactionTypeMap } from './referral-fields/attorney/attorney-transaction-type'
import { contractStatusMap } from './referral-fields/attorney/contract-status'
import { projectTypeMap } from './referral-fields/contractor/project-type'
import { creditScoreMap } from './referral-fields/loan-officer/credit-score'
import { employmentStatusMap } from './referral-fields/loan-officer/employment-status'
import type { LoanPurpose } from './referral-fields/loan-officer/loan-purpose'
import { loanPurposeMap } from './referral-fields/loan-officer/loan-purpose'
import { loanTypeMap } from './referral-fields/loan-officer/loan-type'
import { refinanceReasonMap } from './referral-fields/loan-officer/refinance-reason'
import { clientTypeMap } from './referral-fields/real-estate-agent/client-type'
import { propertyTypeMap } from './referral-fields/shared/property-type'
import { timeframeMap } from './referral-fields/shared/timeframe'
import { dateFlexibilityMap } from './referral-fields/title-company-rep/date-flexibility'
import { titleTransactionTypeMap } from './referral-fields/title-company-rep/title-transaction-type'
import type { ReferralDataField, ReferralData } from './referral-types'

export type ReferralFieldType =
  | 'string'
  | 'phone'
  | 'enum'
  | 'currency'
  | 'number'
  | 'list'
  | 'boolean'
  | 'priceRange'
  | 'text'
  | 'date'

export interface ClaimItem {
  type: string
  amount: number
}

export interface ReferralField {
  label: string
  type: ReferralFieldType
  icon: LucideIcon
  requiresApproval: boolean
  map?: Record<string, MapItem>
  getMap?: (data?: ReferralData) => Record<string, MapItem>
}

export const referralFields: Record<ReferralDataField, ReferralField> = {
  clientType: {
    label: 'Client Type',
    type: 'enum',
    map: clientTypeMap,
    icon: User,
    requiresApproval: true,
  },
  clientFirstName: {
    label: 'Client First Name',
    type: 'string',
    icon: User,
    requiresApproval: true,
  },
  clientLastName: {
    label: 'Client Last Name',
    type: 'string',
    icon: User,
    requiresApproval: true,
  },
  clientEmail: {
    label: 'Client Email',
    type: 'string',
    icon: Mail,
    requiresApproval: true,
  },
  clientPhone: {
    label: 'Client Phone',
    type: 'phone',
    icon: Phone,
    requiresApproval: true,
  },
  propertyStreetAddress1: {
    label: 'Street Address',
    type: 'string',
    icon: MapPin,
    requiresApproval: true,
  },
  propertyStreetAddress2: {
    label: 'Address Line 2',
    type: 'string',
    icon: MapPin,
    requiresApproval: true,
  },
  propertyCity: {
    label: 'City',
    type: 'string',
    icon: MapPin,
    requiresApproval: false,
  },
  propertyState: {
    label: 'State',
    type: 'string',
    icon: MapPin,
    requiresApproval: false,
  },
  propertyZip: {
    label: 'ZIP Code',
    type: 'string',
    icon: MapPin,
    requiresApproval: false,
  },
  propertyType: {
    label: 'Property Type',
    type: 'enum',
    map: propertyTypeMap,
    icon: Home,
    requiresApproval: false,
  },
  descriptionOfWork: {
    label: 'Description of Work Needed',
    type: 'string',
    icon: Clipboard,
    requiresApproval: false,
  },
  knownPropertyIssues: {
    label: 'Known Property Issues',
    type: 'text',
    icon: Clipboard,
    requiresApproval: false,
  },
  loanPurpose: {
    label: 'Loan Purpose',
    type: 'enum',
    map: loanPurposeMap,
    icon: DollarSign,
    requiresApproval: false,
  },
  refinanceReason: {
    label: 'Refinance Reason',
    type: 'enum',
    map: refinanceReasonMap,
    icon: MousePointerClick,
    requiresApproval: false,
  },
  downPaymentEstimate: {
    label: 'Down Payment Estimate',
    type: 'currency',
    icon: DollarSign,
    requiresApproval: false,
  },
  incomeEstimate: {
    label: 'Income Estimate',
    type: 'currency',
    icon: DollarSign,
    requiresApproval: false,
  },
  employmentStatus: {
    label: 'Employment Status',
    type: 'enum',
    map: employmentStatusMap,
    icon: Briefcase,
    requiresApproval: false,
  },
  creditScoreEstimate: {
    label: 'Credit Score',
    type: 'enum',
    map: creditScoreMap,
    icon: Shield,
    requiresApproval: false,
  },
  loanType: {
    label: 'Loan Type',
    type: 'enum',
    map: {},
    icon: DollarSign,
    requiresApproval: false,
    getMap: (data?: ReferralData) => {
      const purpose = data?.loanPurpose as LoanPurpose
      return purpose ? loanTypeMap[purpose] : {}
    },
  },
  timeframe: {
    label: 'Timeframe',
    type: 'enum',
    map: timeframeMap,
    icon: Calendar,
    requiresApproval: false,
  },
  priceRange: {
    label: 'Price Range',
    type: 'priceRange',
    icon: DollarSign,
    requiresApproval: false,
  },
  desiredLocations: {
    label: 'General Locations',
    type: 'list',
    icon: MapPin,
    requiresApproval: false,
  },
  attorneyTransactionType: {
    label: 'Transaction Type',
    type: 'enum',
    map: attorneyTransactionTypeMap,
    icon: FileText,
    requiresApproval: false,
  },
  appraiserTransactionType: {
    label: 'Appraisal Purpose',
    type: 'enum',
    map: appraiserTransactionTypeMap,
    icon: FileText,
    requiresApproval: false,
  },
  titleTransactionType: {
    label: 'Transaction Type',
    type: 'enum',
    map: titleTransactionTypeMap,
    icon: FileText,
    requiresApproval: false,
  },
  projectType: {
    label: 'Project Type',
    type: 'enum',
    map: projectTypeMap,
    icon: Home,
    requiresApproval: false,
  },
  contractStatus: {
    label: 'Contract Status',
    type: 'enum',
    map: contractStatusMap,
    icon: Clipboard,
    requiresApproval: false,
  },
  dateFlexibility: {
    label: 'Date Flexibility',
    type: 'enum',
    map: dateFlexibilityMap,
    icon: Calendar,
    requiresApproval: false,
  },
  isPreApproved: {
    label: 'Pre-Approved',
    type: 'boolean',
    icon: Clipboard,
    requiresApproval: false,
  },
  pastClaims: {
    label: 'Past Claims',
    type: 'list',
    icon: Clipboard,
    requiresApproval: false,
  },
  bedrooms: {
    label: 'Bedrooms',
    type: 'number',
    icon: Home,
    requiresApproval: false,
  },
  bathrooms: {
    label: 'Bathrooms',
    type: 'number',
    icon: Home,
    requiresApproval: false,
  },
  budget: {
    label: 'Budget Range',
    type: 'priceRange',
    icon: DollarSign,
    requiresApproval: false,
  },
  squareFootageEstimate: {
    label: 'Square Footage',
    type: 'number',
    icon: Home,
    requiresApproval: false,
  },
  yearBuilt: {
    label: 'Year Built',
    type: 'number',
    icon: Home,
    requiresApproval: false,
  },
  lotSize: {
    label: 'Lot Size',
    type: 'number',
    icon: Home,
    requiresApproval: false,
  },
  uniqueFeatures: {
    label: 'Unique Features',
    type: 'text',
    icon: Clipboard,
    requiresApproval: false,
  },
  materialPreferences: {
    label: 'Material Preferences',
    type: 'text',
    icon: Clipboard,
    requiresApproval: false,
  },
  desiredStartDate: {
    label: 'Desired Start Date',
    type: 'string',
    icon: Calendar,
    requiresApproval: false,
  },
  desiredEndDate: {
    label: 'Desired End Date',
    type: 'string',
    icon: Calendar,
    requiresApproval: false,
  },
  desiredClosingDate: {
    label: 'Desired Closing Date',
    type: 'date',
    icon: Calendar,
    requiresApproval: false,
  },
  additionalServices: {
    label: 'Additional Services',
    type: 'text',
    icon: Clipboard,
    requiresApproval: false,
  },
  dwellingCoverage: {
    label: 'Dwelling Coverage',
    type: 'currency',
    icon: Shield,
    requiresApproval: false,
  },
  liabilityCoverage: {
    label: 'Liability Coverage',
    type: 'currency',
    icon: Shield,
    requiresApproval: false,
  },
  personalPropertyCoverage: {
    label: 'Personal Property Coverage',
    type: 'currency',
    icon: Shield,
    requiresApproval: false,
  },
  deductibleAmount: {
    label: 'Deductible Amount',
    type: 'currency',
    icon: DollarSign,
    requiresApproval: false,
  },
  additionalEndorsements: {
    label: 'Additional Endorsements',
    type: 'currency',
    icon: Shield,
    requiresApproval: false,
  },
  carrierName: {
    label: 'Carrier Name',
    type: 'string',
    icon: Briefcase,
    requiresApproval: false,
  },
  coverageLevelDetails: {
    label: 'Coverage Level',
    type: 'text',
    icon: Shield,
    requiresApproval: false,
  },
  exactPrice: {
    label: 'Purchase Price',
    type: 'currency',
    icon: DollarSign,
    requiresApproval: false,
  },
  assetsEstimate: {
    label: 'Assets Estimate',
    type: 'currency',
    icon: DollarSign,
    requiresApproval: false,
  },
  estimatedPropertyValue: {
    label: 'Property Value est.',
    type: 'currency',
    icon: DollarSign,
    requiresApproval: false,
  },
  mortgageBalance: {
    label: 'Mortgage Balance',
    type: 'currency',
    icon: DollarSign,
    requiresApproval: false,
  },
  approvedLoanAmount: {
    label: 'Approved Loan Amount',
    type: 'currency',
    icon: DollarSign,
    requiresApproval: false,
  },
  additionalTestNeeded: {
    label: 'Additional Test Needed',
    type: 'text',
    icon: Clipboard,
    requiresApproval: false,
  },
}
